import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './lib/cn';
import { Spinner } from './spinner';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md font-medium font-sans',
    'transition-colors duration-micro ease-out',
    'focus-visible:outline-none focus-visible:shadow-focus',
    'disabled:pointer-events-none disabled:opacity-60',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-interactive text-interactive-on hover:bg-interactive-hover active:bg-interactive-pressed',
        secondary: 'border border-border bg-surface text-text-primary hover:bg-hover-bg',
        ghost: 'bg-transparent text-text-primary hover:bg-hover-bg',
        danger: 'bg-danger-600 text-interactive-on hover:bg-danger-700 focus-visible:shadow-focus-danger',
        'danger-quiet':
          'border border-danger-100 bg-surface text-danger-700 hover:bg-danger-50 focus-visible:shadow-focus-danger',
        link: 'h-auto bg-transparent p-0 text-interactive underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-control-sm px-3 text-base',
        md: 'h-control-md px-4 text-base',
        lg: 'h-control-lg px-5 text-base',
        icon: 'h-control-md w-control-md text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * A request this button started is in flight: it shows a spinner, keeps its
   * label, announces `aria-busy` and cannot be pressed again.
   *
   * Deliberately NOT a second spelling of `disabled`. A loading button keeps
   * its variant's default fill — dimming a control that is actively working
   * reads as "unavailable", which is the opposite of what it is.
   *
   * Ignored together with `asChild`: a `Slot` takes exactly one child, and the
   * element it forwards to is usually an `<a>`, which has no `disabled`.
   */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    if (loading && asChild && process.env.NODE_ENV !== 'production') {
      // Silently dropping it would leave a button that never shows it is busy,
      // which is indistinguishable from a broken request.
      console.error('Button: `loading` is ignored when `asChild` is set — a Slot takes exactly one child.');
    }

    // `asChild` forwards to a Slot, which requires exactly ONE child and whose
    // target is usually an <a> — no spinner sibling, and no `disabled` /
    // `aria-busy` invented for an element that has neither. The branch is
    // structural rather than a conditional prop, so this path stays byte-for-byte
    // what it rendered before `loading` existed.
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }), loading && 'disabled:opacity-100')}
        ref={ref}
        aria-busy={loading || undefined}
        {...props}
        // After the spread: a caller's `disabled` must not be able to unset the
        // lockout a loading button needs.
        disabled={props.disabled === true || loading}
      >
        {loading && <Spinner size={size === 'lg' ? 'lg' : size === 'sm' ? 'sm' : 'md'} />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
